import React, { useState, useEffect } from "react";
import { getPartnersAdmin, createPartner, updatePartner, deletePartner, uploadImage } from "../../../APIClient/apiClient";
import { resolveImageSrc } from "../../../utils/resolveImageSrc";
import Toast from "../../../component/Toast";
import "../../../style/admin/AnnouncementManager.css";

const PartnersManager = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ message: "", type: "success" });
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [activeTab, setActiveTab] = useState("en");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    en: { name: "", description: "" },
    vi: { name: "", description: "" },
    slug: "",
    logo_url: "",
    website_url: "",
    sort_order: 0,
    is_active: true,
  });

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    setLoading(true);
    try {
      const data = await getPartnersAdmin();
      setPartners(data);
    } catch (err) {
      setToast({ message: "Failed to load partners", type: "error" });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (language, field, value) => {
    setFormData({
      ...formData,
      [language]: {
        ...formData[language],
        [field]: value,
      },
    });
  };

  const handleMetaChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  const handleImageFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setToast({ message: "Please select an image file", type: "error" });
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setToast({ message: "Image size must be less than 5MB", type: "error" });
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload image
    setUploadingImage(true);
    try {
      const result = await uploadImage(file);
      setFormData({
        ...formData,
        logo_url: result.imageUrl,
      });
      setToast({ message: "Image uploaded successfully", type: "success" });
    } catch (err) {
      setToast({ message: err.message || "Failed to upload image", type: "error" });
      setImagePreview(null);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        translations: {
          en: formData.en,
          vi: formData.vi,
        },
        slug: formData.slug,
        logo_url: formData.logo_url,
        website_url: formData.website_url,
        sort_order: formData.sort_order,
        is_active: formData.is_active,
      };

      if (editingId) {
        await updatePartner(editingId, payload);
      } else {
        await createPartner(payload);
      }
      fetchPartners();
      resetForm();
      setToast({ message: "Partner saved successfully", type: "success" });
    } catch (err) {
      setToast({ message: err.message || "Failed to save partner", type: "error" });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this partner?")) {
      try {
        await deletePartner(id);
        fetchPartners();
        setToast({ message: "Partner deleted successfully", type: "success" });
      } catch (err) {
        setToast({ message: "Failed to delete partner", type: "error" });
      }
    }
  };

  const handleEdit = (partner) => {
    setFormData({
      en: {
        name: partner.translations.en?.name || "",
        description: partner.translations.en?.description || "",
      },
      vi: {
        name: partner.translations.vi?.name || "",
        description: partner.translations.vi?.description || "",
      },
      slug: partner.slug || "",
      logo_url: partner.logo_url || "",
      website_url: partner.website_url || "",
      sort_order: partner.sort_order,
      is_active: partner.is_active,
    });
    setImagePreview(partner.logo_url); // Show current logo
    setEditingId(partner.id);
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      en: { name: "", description: "" },
      vi: { name: "", description: "" },
      slug: "",
      logo_url: "",
      website_url: "",
      sort_order: 0,
      is_active: true,
    });
    setEditingId(null);
    setShowForm(false);
    setActiveTab("en");
    setImagePreview(null);
  };

  if (loading) return <div className="admin-manager-loading">Loading partners...</div>;

  return (
    <div className="admin-manager">
      <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: "", type: "success" })} />

      <div className="admin-manager-header">
        <button className="admin-manager-btn-primary" onClick={() => (showForm ? resetForm() : setShowForm(true))}>
          {showForm ? "Cancel" : "Add New Partner"}
        </button>
      </div>

      {showForm && (
        <div className="admin-manager-form">
          <h3>{editingId ? "Edit Partner" : "Create New Partner"}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-tabs">
              <button type="button" className={activeTab === "en" ? "active" : ""} onClick={() => setActiveTab("en")}>
                English
              </button>
              <button type="button" className={activeTab === "vi" ? "active" : ""} onClick={() => setActiveTab("vi")}>
                Vietnamese
              </button>
            </div>

            {activeTab === "en" && (
              <div className="form-section">
                <div className="form-group">
                  <label>Name (EN) *</label>
                  <input
                    type="text"
                    value={formData.en.name}
                    onChange={(e) => handleInputChange("en", "name", e.target.value)}
                    placeholder="English name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Description (EN)</label>
                  <textarea
                    value={formData.en.description}
                    onChange={(e) => handleInputChange("en", "description", e.target.value)}
                    placeholder="English description"
                    rows="4"
                  />
                </div>
              </div>
            )}

            {activeTab === "vi" && (
              <div className="form-section">
                <div className="form-group">
                  <label>Name (VI) *</label>
                  <input
                    type="text"
                    value={formData.vi.name}
                    onChange={(e) => handleInputChange("vi", "name", e.target.value)}
                    placeholder="Vietnamese name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Description (VI)</label>
                  <textarea
                    value={formData.vi.description}
                    onChange={(e) => handleInputChange("vi", "description", e.target.value)}
                    placeholder="Vietnamese description"
                    rows="4"
                  />
                </div>
              </div>
            )}

            <div className="form-section">
              <h4>General Information</h4>
              <div className="form-group">
                <label>Slug</label>
                <input type="text" value={formData.slug} onChange={(e) => handleMetaChange("slug", e.target.value)} placeholder="partner-slug" />
              </div>
              <div className="form-group">
                <label>Logo Image</label>
                <input type="file" accept="image/*" onChange={handleImageFileChange} disabled={uploadingImage} />
                {uploadingImage && <p style={{ color: "blue", marginTop: "5px" }}>Uploading image...</p>}
                {imagePreview && (
                  <div style={{ marginTop: "10px" }}>
                    <img
                      src={resolveImageSrc(imagePreview)}
                      alt="Logo preview"
                      style={{ maxWidth: "200px", maxHeight: "100px", objectFit: "contain" }}
                    />
                  </div>
                )}
                {formData.logo_url && (
                  <div style={{ marginTop: "5px", fontSize: "12px", color: "#666" }}>
                    <strong>URL:</strong> {formData.logo_url}
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>Website URL</label>
                <input
                  type="text"
                  value={formData.website_url}
                  onChange={(e) => handleMetaChange("website_url", e.target.value)}
                  placeholder="https://..."
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Sort Order</label>
                <input type="number" value={formData.sort_order} onChange={(e) => handleMetaChange("sort_order", parseInt(e.target.value))} />
              </div>
              <div className="form-group">
                <label>Active</label>
                <input type="checkbox" checked={formData.is_active} onChange={(e) => handleMetaChange("is_active", e.target.checked)} />
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary">
                {editingId ? "Update" : "Create"} Partner
              </button>
              <button type="button" className="btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="admin-manager-table">
        <table>
          <thead>
            <tr>
              <th>No.</th>
              <th>Name (EN)</th>
              <th>Slug</th>
              <th>Website</th>
              <th>Active</th>
              <th>Sort Order</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {partners.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ textAlign: "center", padding: "20px" }}>
                  No partners found
                </td>
              </tr>
            ) : (
              partners.map((partner, index) => (
                <tr key={partner.id}>
                  <td>{index + 1}</td>
                  <td>{partner.translations.en?.name || "N/A"}</td>
                  <td>{partner.slug || "N/A"}</td>
                  <td>
                    {partner.website_url ? (
                      <a href={partner.website_url} target="_blank" rel="noopener noreferrer">
                        Link
                      </a>
                    ) : (
                      "N/A"
                    )}
                  </td>
                  <td>{partner.is_active ? "Yes" : "No"}</td>
                  <td>{partner.sort_order}</td>
                  <td className="action-buttons">
                    <button className="btn-edit" onClick={() => handleEdit(partner)}>
                      Edit
                    </button>
                    <button className="btn-delete" onClick={() => handleDelete(partner.id)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PartnersManager;
